import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  TextField,
  alpha,
} from "@mui/material";
import { LocationOn as LocationOnIcon } from "@mui/icons-material";
import { UpdateGfStoreDto } from "@swagger/typescript-fetch-goflyer";
import { FieldPath, FieldValue } from "../utils/fieldChangeHelpers";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";

interface LocationInformationProps {
  store?: UpdateGfStoreDto;
  isEditing: boolean;
  onFieldChange: (field: FieldPath, value: FieldValue) => void;
}

export const LocationInformation: React.FC<LocationInformationProps> = ({
  store,
  isEditing,
  onFieldChange,
}) => {
  const { t } = useTranslation();

  return (
    <Card
      elevation={1}
      sx={{
        height: "100%",
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        border: "1px solid",
        borderColor: alpha("#000", 0.06),
        overflow: "visible",
      }}
    >
      <CardContent sx={{ p: 3, overflow: "visible" }}>
        <Box display="flex" alignItems="center" mb={2}>
          <LocationOnIcon sx={{ color: "#f58321", mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {t(messages.Location_Information())}
          </Typography>
        </Box>
        <List dense>
          <ListItem disablePadding sx={{ mb: 1 }}>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Address())}
                value={store?.address || ""}
                onChange={(e) => onFieldChange("address", e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={t(messages.Address())}
                secondary={store?.address}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.City())}
                value={store?.city || ""}
                onChange={(e) => onFieldChange("city", e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={t(messages.City())}
                secondary={store?.city}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Province_State())}
                value={store?.province || ""}
                onChange={(e) => onFieldChange("province", e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={t(messages.Province_State())}
                secondary={store?.province}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
          <ListItem disablePadding sx={{ mb: 1 }}>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Country())}
                value={store?.country || ""}
                onChange={(e) => onFieldChange("country", e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={t(messages.Country())}
                secondary={store?.country}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
          <ListItem disablePadding>
            {isEditing ? (
              <TextField
                fullWidth
                label={t(messages.Postal_Code())}
                value={store?.zip || ""}
                onChange={(e) => onFieldChange("zip", e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={t(messages.Postal_Code())}
                secondary={store?.zip}
                primaryTypographyProps={{
                  fontSize: "0.875rem",
                  fontWeight: 500,
                  color: "text.secondary",
                }}
                secondaryTypographyProps={{
                  fontSize: "1rem",
                  fontWeight: 500,
                  color: "text.primary",
                }}
              />
            )}
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};
